import { useLayoutEffect, useRef, useState, type CSSProperties, type SyntheticEvent } from 'react';
import { cx } from '@/lib/cx';

const TILE = 256;
const MIN_ZOOM = 10;
const MAX_ZOOM = 19;
const MAX_LAT = 85.05112878;

/** Attribution required wherever Esri World Imagery tiles appear. */
export function ImageryCredit({ className }: { className?: string }) {
  return <span className={cx('imagery-credit', className)}>Imagery: Esri, Maxar</span>;
}

function toNumber(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Web Mercator pixel position of a point at zoom z (256px tiles). */
function project(lat: number, lon: number, z: number) {
  const n = TILE * 2 ** z;
  const s = Math.sin((Math.max(-MAX_LAT, Math.min(MAX_LAT, lat)) * Math.PI) / 180);
  return {
    x: ((lon + 180) / 360) * n,
    y: (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * n,
  };
}

interface Tile {
  key: string;
  src: string;
  left: number;
  top: number;
}

/**
 * Tiles around the point, at least 2x2 (more only when the box is larger than one tile), placed so
 * the point sits at the centre of a w x h box. Rows outside the world are skipped; columns wrap.
 */
function tilesFor(lat: number, lon: number, z: number, w: number, h: number): Tile[] {
  const { x, y } = project(lat, lon, z);
  const count = 2 ** z;
  const halfW = Math.max(w / 2, TILE / 2);
  const halfH = Math.max(h / 2, TILE / 2);
  const x0 = Math.floor((x - halfW) / TILE);
  const x1 = Math.max(x0 + 1, Math.floor((x + halfW - 0.001) / TILE));
  const y0 = Math.floor((y - halfH) / TILE);
  const y1 = Math.max(y0 + 1, Math.floor((y + halfH - 0.001) / TILE));
  const ox = Math.round(w / 2 - x);
  const oy = Math.round(h / 2 - y);
  const out: Tile[] = [];
  for (let ty = y0; ty <= y1; ty++) {
    if (ty < 0 || ty >= count) continue;
    for (let tx = x0; tx <= x1; tx++) {
      const wx = ((tx % count) + count) % count;
      out.push({ key: `${z}/${ty}/${tx}`, src: `/tiles/${z}/${ty}/${wx}`, left: tx * TILE + ox, top: ty * TILE + oy });
    }
  }
  return out;
}

function markLoaded(e: SyntheticEvent<HTMLImageElement>) {
  e.currentTarget.dataset.loaded = 'true';
}

export interface SiteImageProps {
  /** Coordinates as numbers or the API's decimal strings; missing or invalid shows the placeholder. */
  lat: number | string | null | undefined;
  lon: number | string | null | undefined;
  /** Tile zoom, 10 to 19. Default 17 (a site with its surroundings). */
  zoom?: number;
  /** Box size: px number or any CSS length (e.g. '100%'); tiles are laid out for the measured size. */
  width: number | string;
  height: number | string;
  /** Accessible name, e.g. "Satellite view of Kaai 730 warehouse". Empty string: decorative (the name is printed next to it). */
  label: string;
  /** Shown on the placeholder when there are no coordinates. */
  city?: string | null;
  /** Overlay the imagery credit (default true). Pass false only when an ImageryCredit is shown nearby. */
  credit?: boolean;
  /** Ring marking the point at the centre (default true). */
  pin?: boolean;
  className?: string;
}

/** Satellite image of a site: Esri World Imagery tiles stitched through our /tiles proxy, lazy loaded. */
export function SiteImage({ lat, lon, zoom = 17, width, height, label, city, credit = true, pin = true, className }: SiteImageProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(() =>
    typeof width === 'number' && typeof height === 'number' ? { w: width, h: height } : null,
  );

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const w = Math.round(el.clientWidth);
      const h = Math.round(el.clientHeight);
      setSize((s) => (s && s.w === w && s.h === h ? s : { w, h }));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const la = toNumber(lat);
  const lo = toNumber(lon);
  const located = la !== null && lo !== null && Math.abs(la) <= 90 && Math.abs(lo) <= 180;
  const z = Math.round(Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom)));
  const tiles = located && size && size.w > 0 && size.h > 0 ? tilesFor(la, lo, z, size.w, size.h) : [];
  const style: CSSProperties = { width, height };

  return (
    <div
      ref={ref}
      className={cx('site-image', className)}
      style={style}
      role={label ? 'img' : undefined}
      aria-label={label ? (located ? label : `${label}. No satellite image yet`) : undefined}
      aria-hidden={label ? undefined : true}
    >
      {located ? (
        <>
          <div className="site-image-tiles" aria-hidden>
            {tiles.map((t) => (
              <img
                key={t.key}
                className="site-image-tile"
                src={t.src}
                alt=""
                width={TILE}
                height={TILE}
                loading="lazy"
                decoding="async"
                draggable={false}
                style={{ left: t.left, top: t.top }}
                onLoad={markLoaded}
              />
            ))}
          </div>
          {pin ? <span className="site-image-pin" aria-hidden /> : null}
          {credit ? <ImageryCredit /> : null}
        </>
      ) : (
        <div className="site-image-empty" aria-hidden>
          <span>{city?.trim() || 'Not on the map yet'}</span>
        </div>
      )}
    </div>
  );
}
